import React, { useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Button,
  IconButton,
  InputLabel,
  Stack,
  Typography,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import {
  getPasswordConfirmValidation,
  emailValidation,
  ChangePasswordValidation,
  nameValidation,
  required,
  phoneNumberValidation,
} from "models/validations";
import { GlobalContext } from "store/globalContext";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { FormCheckbox } from "UI/FormCheckbox";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { useSpecialists } from "hooks/useSpecialists";
import { useUserLocations } from "hooks/useUserLocations";
import { LocationSelector } from "UI/LocationSelector";

/** @type {import("types/commonTypes").NewUserData & { id: string; userIdForReassign: string; }}*/
const defaultFormData = {
  id: "",
  role: "",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  password_repeat: "",
  marketingRegionId: "",
  userIdForReassign: "",
};

export const EditUserDialog = ({
  selectedUser,
  handleClose,
  refreshList,
  regionsOptions = {},
  refreshRegionsOptions = () => null,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, userData, openConfirmationWithCallback } =
    useContext(GlobalContext);
  const [isChangePasswordVisible, setIsChangePasswordVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [assignedButtonLocations, setAssignedButtonLocations] = useState([]);

  /** @type {import("types/commonTypes").Organization[]} */
  const initialState = [];
  const [recipients, setRecipients] = useState(initialState);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);

  const { specialistsOptions } = useSpecialists();
  const {
    locations,
    selectedLocations,
    isLocationDataLoaded,
    handleChangeLocation,
    isSelectedLocationsChanged,
    resetSelectedLocations,
    updateSelectedLocations,
  } = useUserLocations({ user: selectedUser });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    // defaultValues: defaultFormData,
    values: selectedUser,
  });

  const selectedRole = watch("role");

  const handleForcedClose = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    let noError = true;
    if (isSelectedLocationsChanged) {
      noError = await updateSelectedLocations();
    }

    if (!isDirty || !noError) return;

    const {
      id,
      firstName,
      lastName,
      role,
      password,
      phone,
      email,
      organizationId,
      marketingRegionId,
      smsIsEnabled,
      emailIsEnabled,
    } = getValues();

    const user = {
      id,
      firstName,
      lastName,
      role,
      password,
      phone,
      email,
      organizationId,
      marketingRegionId,
      smsIsEnabled,
      emailIsEnabled,
    };

    const result = await API.post("restApi", "/users", {
      body: {
        user,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            User updated successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleForcedClose();
  });

  const deleteUser = createApiCall(async () => {
    const { id } = getValues();
    const result = await API.del("restApi", `/users/${id}`, {});

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            User deleted successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleForcedClose();
  });

  const closeAndReset = useCloseAndReset(
    handleForcedClose,
    isDirty || isSelectedLocationsChanged
  );

  const handleChangePasswordClick = () => {
    setValue("password", "");

    if (isChangePasswordVisible) {
      setValue("password_repeat", "");
    }

    setIsChangePasswordVisible(!isChangePasswordVisible);
  };

  const handleDeleteUser = async () => {
    // Check if user is assigned to the button location
    const { id } = getValues();
    dispatch(showSpinner());
    const result = await API.get(
      "restApi",
      `/specialist_assignments_by_user/${id}`,
      {}
    );

    dispatch(hideSpinner());

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    if (result?.length > 0) {
      // If user is assigned to the button location, show dialog to reassign user's button location to another user
      setAssignedButtonLocations(result);
      return;
    }

    openConfirmationWithCallback({
      title: "Attention",
      message: "Deleting a user cannot be undone. Continue?",
      callbackFn: () => {
        deleteUser();
      },
      buttonLabels: ["No", "Yes"],
    });
  };

  const reassignAndDeleteUser = () => {
    openConfirmationWithCallback({
      title: "Attention",
      message: "Deleting a user cannot be undone. Continue?",
      callbackFn: async () => {
        // reassign user's button location to another user
        const { id, userIdForReassign } = getValues();
        dispatch(showSpinner());
        const result = await API.post(
          "restApi",
          `/specialist_assignments_by_user/${id}`,
          {
            body: {
              userIdForReassign,
            },
          }
        );
        dispatch(hideSpinner());

        if (result?.error) {
          console.error(result.error);
          dispatch(showError(result.error));
          return;
        }
        deleteUser();
      },
      buttonLabels: ["No", "Yes"],
    });
  };

  const password = useRef("");
  password.current = watch("password", "");

  recipients.sort((a, b) => (a.name > b.name ? 1 : -1));

  useEffect(() => {
    const getData = async () => {
      try {
        const result = await API.get("restApi", "/organizations", {});
        setRecipients(result?.Items ?? []);
      } catch (error) {}
    };

    getData();
  }, []);

  const isSuperAdmin =
    userData?.role === "Super Admin" ||
    userData?.email === "admin@referral-stream.com";

  const roleOptions = isSuperAdmin
    ? {
        "Super Admin": "Super Admin",
        Admin: "Admin",
        Marketer: "Marketer",
        Scheduler: "Scheduler",
      }
    : {
        Admin: "Admin",
        Marketer: "Marketer",
        Scheduler: "Scheduler",
      };

  const role = watch("role");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const id = watch("id");
  const userIdForReassign = watch("userIdForReassign");
  const organizationId = watch("organizationId");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileButtonSx = { padding: "10px 10px" };
  const sx = isMobile ? mobileButtonSx : {};

  if (assignedButtonLocations?.length > 0) {
    const newSpecialistsOptions = Object.entries(specialistsOptions)
      .filter(
        ([_, value]) =>
          value?.organizationId === organizationId &&
          ["Admin", "Marketer", "Scheduler"].includes(value?.role)
      )
      .reduce(
        (acc, [key, { label }]) => ({
          ...acc,
          [key]: label,
        }),
        {}
      );
    delete newSpecialistsOptions[id];

    return (
      <DialogWindow
        open={true}
        withCloseButton
        handleClose={closeAndReset}
        title={"Reassign existing locations"}
        content={
          <Stack mt={1} gap={1}>
            <Box>
              {firstName} {lastName} is assigned to{" "}
              {assignedButtonLocations.length === 1
                ? `the button location`
                : `${assignedButtonLocations.length} locations`}
              . Before deleting, please select another user to assign.
            </Box>
            <Stack alignItems={"flex-start"} flex={1}>
              <FormSelectField
                id="userIdForReassign"
                name="userIdForReassign"
                control={control}
                options={newSpecialistsOptions}
                withNone={false}
                rules={{ required }}
              />
            </Stack>
          </Stack>
        }
        actions={
          <Stack
            mt={2}
            mb={2}
            width={"100%"}
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Button
              variant="outlined"
              onClick={() => setAssignedButtonLocations([])}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={reassignAndDeleteUser}
              disabled={!userIdForReassign}
            >
              Reassign and Delete User
            </Button>
          </Stack>
        }
      />
    );
  }

  return (
    <DialogWindow
      open={!!selectedUser?.id}
      withCloseButton
      handleClose={closeAndReset}
      title={"Edit User"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={1} gap={1}>
            {selectedUser?.id && selectedUser?.organization && (
              <Box textAlign={"left"} className="text-sm sm:text-lg">
                Organization: {selectedUser?.organization}
              </Box>
            )}
            <Stack direction={"row"} justifyContent={"space-between"} gap={2}>
              <Stack alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="role">Role</InputLabel>
                <FormSelectField
                  id="role"
                  name="role"
                  control={control}
                  options={roleOptions}
                  withNone={false}
                  rules={{ required }}
                />
              </Stack>
            </Stack>
            <Stack direction={"row"} gap={2} width={"100%"}>
              <Stack alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="firstName">First name</InputLabel>
                <FormTextField
                  id="firstName"
                  name="firstName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                    form: {
                      autocomplete: "off",
                    },
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="lastName">Last name</InputLabel>
                <FormTextField
                  id="lastName"
                  name="lastName"
                  control={control}
                  rules={nameValidation}
                  InputProps={{
                    autoComplete: "off",
                    form: {
                      autocomplete: "off",
                    },
                  }}
                />
              </Stack>
            </Stack>
            {["Marketer", "Scheduler"].includes(selectedRole) && (
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="phone">Phone</InputLabel>
                <FormMaskedTextField
                  id="phone"
                  name="phone"
                  control={control}
                  rules={phoneNumberValidation}
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            )}
            {selectedRole === "Marketer" && !isSuperAdmin && (
              <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                <Stack alignItems={"flex-start"} flex="1">
                  <InputLabel htmlFor="marketingRegionId">
                    Marketing Region
                  </InputLabel>
                  <FormSelectField
                    id="marketingRegionId"
                    name="marketingRegionId"
                    control={control}
                    options={regionsOptions}
                  />
                </Stack>
                <Button onClick={() => setIsAddNewRegionDialogOpen(true)}>
                  Add new region
                </Button>
              </Stack>
            )}
            {["Marketer", "Scheduler"].includes(selectedRole) &&
              isLocationDataLoaded && (
                <LocationSelector
                  locations={locations}
                  selectedLocationIds={selectedLocations}
                  handleChangeLocation={handleChangeLocation}
                  resetSelectedLocations={resetSelectedLocations}
                  isSelectedLocationsChanged={isSelectedLocationsChanged}
                />
              )}
            {selectedRole === "Marketer" && isSuperAdmin && (
              <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                <Stack alignItems={"flex-start"} flex="1">
                  <InputLabel htmlFor="marketingRegionId">
                    Marketing Region of Recipient
                  </InputLabel>
                  <FormTextField
                    id="lastNammarketingRegionIde"
                    name="label"
                    control={control}
                    disabled
                  />
                </Stack>
              </Stack>
            )}
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="email">Email</InputLabel>
              <FormTextField
                id="email"
                name="email"
                control={control}
                rules={emailValidation}
                InputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                autoComplete="off"
                disabled
              />
            </Stack>
            {["Marketer", "Scheduler", "Originator"].includes(role) && (
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flexWrap={"wrap"}
                columnGap={2}
                rowGap={1}
                maxWidth={"600px"}
              >
                Notification configuration:
                <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
                  <Stack alignItems={"flex-start"}>
                    <FormCheckbox
                      name="smsIsEnabled"
                      control={control}
                      label={"SMS enabled"}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"}>
                    <FormCheckbox
                      name="emailIsEnabled"
                      control={control}
                      label={"Email enabled"}
                    />
                  </Stack>
                </Stack>
              </Stack>
            )}
            {isChangePasswordVisible && (
              <>
                <Divider />
                <Stack direction={"row"} gap={2}>
                  <Stack alignItems={"flex-start"} flex={1}>
                    <InputLabel htmlFor="change-password">
                      Change password
                    </InputLabel>
                    <FormTextField
                      id="change-password"
                      name="password"
                      control={control}
                      rules={ChangePasswordValidation}
                      type={isPasswordVisible ? "text" : "password"}
                      InputProps={{
                        autocomplete: "new-password",
                        form: {
                          autocomplete: "off",
                        },
                      }}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"} flex={1}>
                    <InputLabel htmlFor="password_repeat">
                      Repeat password
                    </InputLabel>
                    <FormTextField
                      id="password_repeat"
                      name="password_repeat"
                      control={control}
                      rules={getPasswordConfirmValidation(password.current)}
                      type={isPasswordVisible ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            color="info"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                        autoComplete: "off",
                        form: {
                          autocomplete: "off",
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
        </form>
      }
      actions={
        <Stack
          mt={2}
          mb={2}
          width={"100%"}
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={1}
        >
          <Button
            variant="outlined"
            onClick={handleDeleteUser}
            color={"warning"}
            disabled={isDirty}
            sx={sx}
          >
            Delete User
          </Button>
          <Button
            variant="outlined"
            onClick={handleChangePasswordClick}
            sx={sx}
          >
            {isChangePasswordVisible
              ? "Close Password Change"
              : "Change Password"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty && !isSelectedLocationsChanged}
            sx={sx}
          >
            Update User
          </Button>
        </Stack>
      }
    />
  );
};
